import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminJobViewModal from "@/components/AdminJobViewModal";
import AdvancedEditJobModal from "@/components/AdvancedEditJobModal";
import { Eye, Edit, X, Check, Clock } from "lucide-react";
import { toast } from 'sonner';

// Job-type pill colors (per design prototype)
const JOB_TYPE = {
  WEDDINGS:    { label: 'Wedding',     fg: '#BE185D', bg: '#FCE7F3' },
  CORPORATE:   { label: 'Corporate',   fg: '#2563EB', bg: '#DBEAFE' },
  EVENTS:      { label: 'Events',      fg: '#C2410C', bg: '#FFEDD5' },
  REAL_ESTATE: { label: 'Real Estate', fg: '#0F766E', bg: '#CCFBF1' },
  OTHERS:      { label: 'Other',       fg: '#475569', bg: '#E2E8F0' },
};

const AdminJobApproval = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [eligibleUsersCount, setEligibleUsersCount] = useState(0);
  const [pushUsersCount, setPushUsersCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(false);

  useEffect(() => {
    const isAdmin = localStorage.getItem('shootzu_admin');
    if (!isAdmin) navigate('/szadmin');

    fetchPendingJobs();
  }, []);

  const fetchPendingJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/pending-approval`
      );

      if (response.data.success) {
        setJobs(response.data.jobs || []);
      }
    } catch (error) {
      console.error('Failed to fetch pending jobs:', error);
      toast.error('Failed to load pending jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (job) => {
    setSelectedJob(job);
    setShowJobDetails(true);
  };

  const handleEditJob = (job) => {
    setSelectedJob(job);
    setShowEditModal(true);
  };

  const handleCancelJob = async (jobId, reason = '') => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/${jobId}/cancel`,
        { reason }
      );

      toast.success('Job cancelled by admin');
      fetchPendingJobs();
    } catch (error) {
      console.error('Error cancelling job:', error);
      toast.error('Failed to cancel job');
    }
  };

  const handleApproveJob = async (job) => {
    setSelectedJob(job);
    setShowApprovalModal(true);
    setLoadingCount(true);

    // Fetch eligible users count (email and push)
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/count-job-notification-recipients/${job.id}`
      );
      setEligibleUsersCount(response.data.email_count || response.data.count || 0);
      setPushUsersCount(response.data.push_count || 0);
    } catch (error) {
      console.error('Failed to fetch eligible users count:', error);
      setEligibleUsersCount(0);
      setPushUsersCount(0);
    } finally {
      setLoadingCount(false);
    }
  };

  const handleApprovalDecision = async (jobId, shouldNotify = true) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/${jobId}/approve`,
        {
          notify_users: shouldNotify,
          admin_email: 'hello@shootzu.com'
        }
      );

      toast.success(`Job approved ${shouldNotify ? 'and users notified' : 'without notifications'}`);
      setShowApprovalModal(false);
      fetchPendingJobs();
    } catch (error) {
      console.error('Error approving job:', error);
      toast.error('Failed to approve job');
    }
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Job type config lookup
  const getJobType = (type) => JOB_TYPE[type] || { label: type || 'Other', fg: '#475569', bg: '#E2E8F0' };

  // Derive 4-up meta strip values for a job
  const getMeta = (job) => {
    const jobDates = job.dates || (job.date ? [job.date] : []);
    const crewMembers = job.crew_members || [];
    const totalHours = crewMembers.reduce((total, m) => {
      if (m.dateAssignments && m.dateAssignments.length) {
        return total + m.dateAssignments.reduce((s, a) => s + (parseFloat(a.hours) || 0), 0);
      }
      return total + (parseFloat(m.hours) || 0);
    }, 0);
    return {
      days: jobDates.length || 1,
      roles: crewMembers.length || 1,
      hours: totalHours || 0,
      budget: formatCurrency(job.budget || job.total),
    };
  };

  const getRoleRows = (job) => {
    const crewMembers = job.crew_members || [];
    return crewMembers.map((m) => {
      const subtotal = m.subtotal || (
        m.dateAssignments
          ? m.dateAssignments.reduce((s, a) => s + ((parseFloat(a.hours) || 0) * (parseFloat(a.ratePerHour) || 0)), 0)
          : (parseFloat(m.hours) || 0) * (parseFloat(m.ratePerHour) || 0)
      );
      return {
        role: (m.role || 'Crew Member').replace(/_/g, ' '),
        rate: formatCurrency(subtotal),
      };
    });
  };

  const countLabel = `${jobs.length} job${jobs.length !== 1 ? 's' : ''} pending approval`;

  return (
    <div className="min-h-full" style={{ background: '#F5F6F9' }}>
      {/* Sticky Header */}
      <header className="sz-header flex items-center justify-between gap-[18px]">
        <div>
          <h1 className="sz-h1" style={{ margin: 0 }}>Job Approval</h1>
          <div style={{ fontSize: '12.5px', color: '#8A90A2', marginTop: '3px' }}>{countLabel}</div>
        </div>
        <span
          className="sz-fade"
          style={{
            display: 'flex', alignItems: 'center', gap: '7px',
            fontSize: '12.5px', fontWeight: 600, fontFamily: 'Poppins',
            color: '#B45309', background: '#FEF3C7', padding: '8px 14px', borderRadius: '999px',
          }}
        >
          <Clock className="w-[15px] h-[15px]" />
          {jobs.length} awaiting review
        </span>
      </header>

      {/* Scroll body */}
      <main style={{ padding: '22px 30px 56px', maxWidth: '1280px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: '#8A90A2' }}>
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-[#E6E9EF] border-t-[#572B7C]"></div>
            <p style={{ marginTop: '16px', fontSize: '13.5px' }}>Loading pending jobs...</p>
          </div>
        ) : jobs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 20px' }} className="sz-fade">
            <div
              style={{
                width: '72px', height: '72px', margin: '0 auto 18px', borderRadius: '20px',
                background: '#DCFCE7', color: '#15803D',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Check className="w-[38px] h-[38px]" strokeWidth={2} />
            </div>
            <h2 style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: '19px', margin: '0 0 6px' }}>
              All caught up
            </h2>
            <p style={{ fontSize: '13.5px', color: '#8A90A2', margin: 0 }}>
              No jobs are awaiting approval right now.
            </p>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
              gap: '18px',
            }}
          >
            {jobs.map((job) => {
              const t = getJobType(job.job_type);
              const meta = getMeta(job);
              const metaItems = [
                { k: 'days', v: meta.days },
                { k: 'roles', v: meta.roles },
                { k: 'hours', v: meta.hours },
                { k: 'budget', v: meta.budget },
              ];
              const roleRows = getRoleRows(job);
              const postedBy = job.owner?.name || job.owner_name || job.client_name || 'Unknown';
              const jobId = job.job_number || `JOB-${job.id?.substring(0, 6).toUpperCase()}`;

              return (
                <div
                  key={job.id}
                  className="sz-card sz-card-hover sz-fade"
                  style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                >
                  {/* Top: badges + id + title + posted by */}
                  <div style={{ padding: '18px 20px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '10.5px', fontWeight: 700, fontFamily: 'Poppins', color: '#B45309', background: '#FEF3C7', padding: '4px 10px', borderRadius: '999px' }}>
                          PENDING
                        </span>
                        <span style={{ fontSize: '10.5px', fontWeight: 600, fontFamily: 'Poppins', color: t.fg, background: t.bg, padding: '4px 10px', borderRadius: '999px' }}>
                          {t.label}
                        </span>
                      </div>
                      <span className="sz-mono" style={{ fontSize: '11px', color: '#A8AEBC' }}>{jobId}</span>
                    </div>
                    <h3 style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: '17px', margin: '13px 0 0', letterSpacing: '-.3px' }}>
                      {job.title}
                    </h3>
                    <div style={{ fontSize: '12.5px', color: '#8A90A2', marginTop: '4px' }}>Posted by {postedBy}</div>
                  </div>

                  {/* 4-up meta strip */}
                  <div
                    style={{
                      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1px',
                      background: '#EEF0F4', margin: '16px 0 0',
                      borderTop: '1px solid #EEF0F4', borderBottom: '1px solid #EEF0F4',
                    }}
                  >
                    {metaItems.map((m) => (
                      <div key={m.k} style={{ background: '#FAFBFC', padding: '11px 12px', textAlign: 'center' }}>
                        <div style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: '15px' }}>{m.v}</div>
                        <div style={{ fontSize: '10.5px', color: '#8A90A2', marginTop: '2px' }}>{m.k}</div>
                      </div>
                    ))}
                  </div>

                  {/* Description + role rows */}
                  <div style={{ padding: '14px 20px', flex: 1 }}>
                    <p
                      style={{
                        fontSize: '12.5px', lineHeight: 1.55, color: '#2B3142', margin: 0,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}
                    >
                      {job.description || job.brief || 'No description provided'}
                    </p>
                    {roleRows.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '13px' }}>
                        {roleRows.map((r, i) => (
                          <div
                            key={i}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              fontSize: '12px', padding: '7px 11px', background: '#F7F8FB', borderRadius: '9px',
                            }}
                          >
                            <span style={{ color: '#2B3142', fontWeight: 500, textTransform: 'capitalize' }}>{r.role}</span>
                            <span style={{ fontFamily: 'Poppins', fontWeight: 600 }}>{r.rate}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer actions */}
                  <div style={{ display: 'flex', gap: '8px', padding: '14px 20px', borderTop: '1px solid #F1F3F7' }}>
                    <button
                      onClick={() => handleViewDetails(job)}
                      title="View"
                      className="sz-btn sz-btn-outline"
                      style={{ flex: '0 0 auto', width: '40px', height: '40px', padding: 0 }}
                    >
                      <Eye className="w-[17px] h-[17px]" />
                    </button>
                    <button
                      onClick={() => handleEditJob(job)}
                      title="Edit"
                      className="sz-btn sz-btn-outline"
                      style={{ flex: '0 0 auto', width: '40px', height: '40px', padding: 0 }}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleApproveJob(job)}
                      className="sz-btn sz-btn-primary"
                      style={{ flex: 1, height: '40px' }}
                    >
                      <Check className="w-[15px] h-[15px]" strokeWidth={2.4} />
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Cancel job "${job.title}"? This cannot be undone.`)) {
                          handleCancelJob(job.id, 'Cancelled by admin');
                        }
                      }}
                      title="Cancel"
                      className="sz-btn"
                      style={{ flex: '0 0 auto', width: '40px', height: '40px', padding: 0, background: '#fff', border: '1px solid #FECACA', color: '#DC2626' }}
                    >
                      <X className="w-4 h-4" strokeWidth={2.2} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Admin Job Details View Modal */}
      <AdminJobViewModal
        job={selectedJob}
        isOpen={showJobDetails}
        onClose={() => { setShowJobDetails(false); setSelectedJob(null); }}
        onApprove={(job) => { setShowJobDetails(false); handleApproveJob(job); }}
        onEdit={(job) => { setShowJobDetails(false); handleEditJob(job); }}
        onCancel={(jobId, reason) => { handleCancelJob(jobId, reason); setShowJobDetails(false); setSelectedJob(null); }}
      />

      {/* Edit Modal (shared with consumer — do not restyle) */}
      {showEditModal && selectedJob && (
        <AdvancedEditJobModal
          job={selectedJob}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedJob(null);
          }}
          onUpdate={() => {
            fetchPendingJobs();
            setShowEditModal(false);
            setSelectedJob(null);
            toast.success('Job updated by admin');
          }}
        />
      )}

      {/* Inline Approval Decision Modal */}
      {showApprovalModal && selectedJob && (
        <div
          className="sz-modal-backdrop"
          style={{ zIndex: 100 }}
          onClick={() => setShowApprovalModal(false)}
        >
          <div
            className="sz-modal-card"
            style={{ width: '100%', maxWidth: '440px', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sz-modal-header-grad" style={{ padding: '22px 24px' }}>
              <h3 style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: '17px', margin: 0 }}>Approve this job?</h3>
              <div style={{ fontSize: '12.5px', color: 'rgba(255,255,255,.8)', marginTop: '3px' }}>{selectedJob.title}</div>
            </div>
            <div style={{ padding: '18px 20px' }}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <div style={{ flex: 1, textAlign: 'center', padding: '13px', background: '#F7F8FB', borderRadius: '12px' }}>
                  <div style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: '20px', color: '#572B7C' }}>
                    {loadingCount ? '…' : eligibleUsersCount}
                  </div>
                  <div style={{ fontSize: '11px', color: '#8A90A2', marginTop: '2px' }}>emails</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center', padding: '13px', background: '#F7F8FB', borderRadius: '12px' }}>
                  <div style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: '20px', color: '#78287C' }}>
                    {loadingCount ? '…' : pushUsersCount}
                  </div>
                  <div style={{ fontSize: '11px', color: '#8A90A2', marginTop: '2px' }}>push</div>
                </div>
              </div>

              <button
                onClick={() => handleApprovalDecision(selectedJob.id, true)}
                className="sz-btn sz-btn-primary"
                style={{ width: '100%', padding: '13px', marginBottom: '9px' }}
              >
                Approve &amp; Notify
              </button>
              <button
                onClick={() => handleApprovalDecision(selectedJob.id, false)}
                className="sz-btn sz-btn-outline"
                style={{ width: '100%', padding: '12px', marginBottom: '9px' }}
              >
                Approve Silently
              </button>
              <button
                onClick={() => setShowApprovalModal(false)}
                style={{
                  width: '100%', padding: '11px', border: 'none', borderRadius: '12px',
                  background: 'transparent', color: '#8A90A2', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJobApproval;
